import { Middleware, useReducerWithMiddleware } from "#webapp/src/hooks/useReducerWithMiddleware";
import { MutationTuple } from "@apollo/client";
import { RecurrenceRule } from "@clockwise/client-commons/src/datatypes/RecurrenceRule";
import { ConferencingType, FlexDetailsInput, FlexRange, ProposalState } from "@clockwise/schema/v2";
import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { isEmpty } from "lodash";
import { DateTime, Interval } from "luxon";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import invariant from "tiny-invariant";
import { TradeoffBlock } from "../reschedule-confirmation-modal/RescheduleConfirmationModal";
import {
  CreatePersistedProposalDocument,
  CreatePersistedProposalMutation,
  CreatePersistedProposalMutationVariables,
  CurrentPersistedProposalDocument,
  UpdatePersistedProposalDocument,
  UpdatePersistedProposalMutation,
  UpdatePersistedProposalMutationVariables,
} from "./apollo/__generated__/CreateProposal.v2.generated";

type Person = {
  email: string;
  displayName: string;
  givenName: string | null;
  familyName: string | null;
  externalImageUrl: string | null;
  isMe: boolean;
  id: string;
};

type Attendee = {
  id: string;
  isOptional: boolean;
  person: Person;
  user: {
    id: string;
  } | null;
};

type CurrentProposal = {
  proposalId: string | null;
  proposalStatus: ProposalState | null;
  title: string;
  startTime: DateTime;
  endTime: DateTime;
  timeZone: string;
  allDay: boolean;
  initialAttendeeIds: string[];
  attendees: Attendee[];
  description: string;
  recurrenceRule: RecurrenceRule | null;
  conferenceType: ConferencingType | null;
  location: string | null;
  initialized: boolean;
  flexDetails: FlexDetailsInput;
  tradeoffBlocks?: TradeoffBlock[];
  meta: {
    isDragging: boolean;
  };
};

type CurrentProposalReadObj = {
  currentProposal: CurrentProposal;
};

type CurrentProposalWriteObj = {
  initializeProposal: (proposal: Omit<CurrentProposal, "initialized">) => void;
  initOrRescheduleProposal: (proposal: Omit<CurrentProposal, "initialized">) => void;
  updateTitle: (title: string) => void;
  updateTime: (startTime: DateTime, endTime: DateTime) => void;
  updateAllDay: (allDay: boolean) => void;
  updateDescription: (description: string) => void;
  setAttendeeCalendarIds: (attendeeCalendarIds: string[]) => void;
  setAttendees: (attendees: Attendee[]) => void;
  addAttendee: (attendee: Attendee) => void;
  removeAttendee: (email: string) => void;
  updateAttendeeOptionality: (email: string, isOptional: boolean) => void;
  updateRecurrenceRule: (recurrenceRule: RecurrenceRule) => void;
  updateConferenceType: (conferenceType: ConferencingType) => void;
  updateLocation: (location: string) => void;
  clearProposal: () => void;
  updateFlexDetails: (flexDetails: FlexDetailsInput) => void;
  updateProposalMeta: (meta: Partial<CurrentProposal["meta"]>) => void;
};

type InitProposalAction = {
  type: "InitProposal";
  proposal: Omit<CurrentProposal, "initialized">;
};

type InitOrRescheduleProposalAction = {
  type: "InitOrRescheduleProposal";
  proposal: Omit<CurrentProposal, "initialized">;
};

type ClearProposalAction = {
  type: "ClearProposal";
};

type UpdateTitleAction = {
  type: "UpdateTitle";
  title: string;
};

type UpdateTimeAction = {
  type: "UpdateTime";
  startTime: DateTime;
  endTime: DateTime;
};

type UpdateAllDayAction = {
  type: "UpdateAllDay";
  allDay: boolean;
};

type UpdateDescriptionAction = {
  description: string;
  type: "UpdateDescription";
};

type SetAttendeeCalendarIdsAction = {
  type: "SetAttendeeCalendarIds";
  initialAttendeeIds: string[];
};

type SetAttendeesAction = {
  type: "SetAttendees";
  attendees: Attendee[];
};

type AddAttendeeAction = {
  type: "AddAttendee";
  attendee: Attendee;
};

type RemoveAttendeeAction = {
  type: "RemoveAttendee";
  email: string;
};

type UpdateAttendeeOptionalityAction = {
  type: "UpdateAttendeeOptionality";
  email: string;
  isOptional: boolean;
};

type UpdateRecurrenceRuleAction = {
  type: "UpdateRecurrenceRule";
  recurrenceRule: RecurrenceRule;
};

type UpdateConferenceTypeAction = {
  type: "UpdateConferenceType";
  conferenceType: ConferencingType | null;
};

type UpdateLocationAction = {
  type: "UpdateLocation";
  location: string;
};

type UpdateFlexDetailsAction = {
  type: "UpdateFlexDetails";
  flexDetails: FlexDetailsInput;
};

type UpdatePersistedProposalAction = {
  type: "UpdatePersistedProposal";
  proposalId: string;
  proposalStatus: ProposalState;
  tradeoffBlocks?: TradeoffBlock[];
};

type UpdateProposalMetaAction = {
  type: "UpdateProposalMeta";
  meta: Partial<CurrentProposal["meta"]>;
};

type ProposalAction =
  | InitProposalAction
  | InitOrRescheduleProposalAction
  | ClearProposalAction
  | UpdateTitleAction
  | UpdateTimeAction
  | UpdateAllDayAction
  | UpdateDescriptionAction
  | SetAttendeeCalendarIdsAction
  | SetAttendeesAction
  | AddAttendeeAction
  | RemoveAttendeeAction
  | UpdateAttendeeOptionalityAction
  | UpdateRecurrenceRuleAction
  | UpdateConferenceTypeAction
  | UpdateLocationAction
  | UpdateFlexDetailsAction
  | UpdatePersistedProposalAction
  | UpdateProposalMetaAction;

function proposalReducer(state: CurrentProposal, action: ProposalAction) {
  switch (action.type) {
    case "InitProposal":
      return { ...action.proposal, initialized: true };
    case "InitOrRescheduleProposal": {
      if (state.initialized) {
        const duration = state.endTime.diff(state.startTime);
        return {
          ...state,
          startTime: action.proposal.startTime,
          endTime: action.proposal.startTime.plus(duration),
        };
      } else {
        return { ...action.proposal, initialized: true };
      }
    }
    case "ClearProposal":
      return { ...INITIAL_PROPOSAL };
    case "UpdateTitle":
      return {
        ...state,
        title: action.title,
      };
    case "UpdateTime":
      return {
        ...state,
        startTime: action.startTime,
        endTime: action.endTime,
      };
    case "UpdateAllDay": {
      if (action.allDay) {
        return {
          ...state,
          allDay: true,
          startTime: state.startTime.startOf("day"),
          endTime: state.startTime.plus({ days: 1 }).startOf("day"),
        };
      } else {
        // Since we set the start time to the start of the day when toggling on all day,
        // we set it back to the current time here to make it easier for the user to "see"
        // the proposal in the calendar when toggling off all day.
        const startTime = state.startTime.set({ hour: DateTime.local().hour });
        return {
          ...state,
          allDay: false,
          startTime,
          endTime: startTime.plus({ minutes: 30 }),
        };
      }
    }
    case "UpdateDescription":
      return {
        ...state,
        description: action.description,
      };
    case "SetAttendeeCalendarIds":
      return {
        ...state,
        initialAttendeeIds: action.initialAttendeeIds, // Changed from attendeeCalendarIds
      };
    case "SetAttendees":
      return {
        ...state,
        attendees: action.attendees,
      };
    case "AddAttendee": {
      return {
        ...state,
        attendees: [...state.attendees, action.attendee],
      };
    }
    case "RemoveAttendee": {
      return {
        ...state,
        attendees: state.attendees.filter((attendee) => attendee.person.email !== action.email),
      };
    }
    case "UpdateAttendeeOptionality": {
      return {
        ...state,
        attendees: state.attendees.map((attendee) =>
          attendee.person.email === action.email
            ? { ...attendee, isOptional: action.isOptional }
            : attendee,
        ),
      };
    }
    case "UpdateRecurrenceRule":
      return {
        ...state,
        recurrenceRule: action.recurrenceRule,
      };
    case "UpdateConferenceType":
      return {
        ...state,
        conferenceType: action.conferenceType,
      };

    case "UpdateLocation":
      return {
        ...state,
        location: action.location,
      };

    case "UpdateFlexDetails":
      return {
        ...state,
        flexDetails: action.flexDetails,
      };
    case "UpdatePersistedProposal":
      return {
        ...state,
        proposalId: action.proposalId,
        proposalStatus: action.proposalStatus,
        tradeoffBlocks: action.tradeoffBlocks,
      };
    case "UpdateProposalMeta":
      return {
        ...state,
        meta: {
          ...state.meta,
          ...action.meta,
        },
      };
  }
}

const INITIAL_PROPOSAL: CurrentProposal = {
  proposalId: null,
  proposalStatus: ProposalState.LoadingSuggestions, // Assume that we're loading immediately.
  title: "",
  startTime: DateTime.local(),
  endTime: DateTime.local().plus({ hours: 1 }),
  timeZone: "America/Los_Angeles",
  allDay: false,
  description: "",
  initialAttendeeIds: [],
  attendees: [],
  recurrenceRule: null,
  conferenceType: null,
  location: null,
  initialized: false,
  flexDetails: {
    isFlexible: true,
    flexRange: FlexRange.Day,
  },
  tradeoffBlocks: undefined,
  meta: {
    isDragging: false, // Set if we are manipulating the proposal with clickDrag
  },
};

type CreateProposalMutationFn = MutationTuple<
  CreatePersistedProposalMutation,
  CreatePersistedProposalMutationVariables
>[0];

type UpdateProposalMutationFn = MutationTuple<
  UpdatePersistedProposalMutation,
  UpdatePersistedProposalMutationVariables
>[0];

const persistMiddleware = (
  createFn: CreateProposalMutationFn,
  updateFn: UpdateProposalMutationFn,
): Middleware<CurrentProposal, ProposalAction> => (
  state: CurrentProposal,
  action: ProposalAction,
) => {
  if (
    action.type === "ClearProposal" ||
    action.type === "UpdatePersistedProposal" ||
    state.meta.isDragging // Don't persist while a user is still dragging to create an event
  ) {
    return;
  }

  if (!state.proposalId) {
    void createFn({
      variables: {
        attendees: state.initialAttendeeIds.map((email, i) => ({
          email,
          optional: false,
          isOrganizer: i === 0,
        })),
        timeRange: Interval.fromDateTimes(state.startTime, state.endTime).toISO(),
        timeZone: getRenderTimeZone(),
        title: state.title,
      },
    });
  } else {
    const variables = buildCompleteUpdateVariables(state.proposalId, state);
    void updateFn({
      variables,
    });
  }
};

function buildCompleteUpdateVariables(
  id: string,
  state: CurrentProposal,
): UpdatePersistedProposalMutationVariables {
  const attendees = isEmpty(state.attendees)
    ? state.initialAttendeeIds.map((email, i) => ({ email, optional: false, isOrganizer: i === 0 }))
    : state.attendees.map((attendee) => ({
        email: attendee.person.email,
        optional: attendee.isOptional,
        isOrganizer: attendee.person.isMe,
      }));

  return {
    id,
    title: state.title,
    timeRange: Interval.fromDateTimes(state.startTime, state.endTime).toISO(),
    timeZone: state.startTime.zone.name,
    attendees,
    description: state.description,
    recurrenceRule: state.recurrenceRule?.toString(),
    conferenceType: state.conferenceType,
    location: state.location,
    flexDetails: state.flexDetails,
  };
}

const ReadContext = createContext<CurrentProposalReadObj>({ currentProposal: INITIAL_PROPOSAL });
const WriteContext = createContext<CurrentProposalWriteObj | null>(null);

export const CurrentProposalProvider = ({ children }: { children: ReactNode }) => {
  const [pollInterval, setPollInterval] = useState(0);
  const [createProposal] = useGatewayMutation(CreatePersistedProposalDocument, {
    onCompleted(data) {
      if (data.createProposal?.id) {
        handleUpdatePersistedProposal(data.createProposal.id, data.createProposal.state);
      }
    },
  });
  const [updateProposal] = useGatewayMutation(UpdatePersistedProposalDocument, {
    onCompleted(data) {
      if (data.updateProposal?.id) {
        const diff = data?.updateProposal?.diffBlocks[0]?.diffs[0];
        const addDiff = diff?.__typename === "AddDiff" ? diff : null;
        const tradeoffBlocks = addDiff?.tradeoffBlocks;
        handleUpdatePersistedProposal(
          data.updateProposal.id,
          data.updateProposal.state,
          tradeoffBlocks,
        );
      }
    },
  });

  const persistFn = useMemo(() => persistMiddleware(createProposal, updateProposal), [
    createProposal,
    updateProposal,
  ]);
  const [proposal, dispatch] = useReducerWithMiddleware(proposalReducer, INITIAL_PROPOSAL, [
    persistFn,
  ]);
  const { data } = useGatewayQuery(CurrentPersistedProposalDocument, {
    variables: { id: proposal?.proposalId! },
    skip: !proposal?.proposalId,
    pollInterval,
  });

  const proposalState = data?.proposal?.state;

  // Poll rapidly when we're still loading suggestions.
  useEffect(() => {
    const pollInterval = proposalState === ProposalState.LoadingSuggestions ? 500 : 0;
    setPollInterval(pollInterval);
  }, [proposalState]);

  const handleInitializeProposal = (proposal: Omit<CurrentProposal, "initialized">) => {
    dispatch({ type: "InitProposal", proposal });
  };

  const handleUpdatePersistedProposal = (
    proposalId: string,
    proposalStatus: ProposalState,
    tradeoffBlocks?: TradeoffBlock[],
  ) => {
    dispatch({ type: "UpdatePersistedProposal", proposalId, proposalStatus, tradeoffBlocks });
  };

  // When we receive new data, either from the server or from the Apollo cache, we need to update the persisted proposal.
  useEffect(() => {
    if (data?.proposal?.id && data.proposal?.state !== ProposalState.LoadingSuggestions) {
      const diff = data.proposal.diffBlocks[0].diffs[0];
      const addDiff = diff.__typename === "AddDiff" ? diff : null;
      handleUpdatePersistedProposal(data.proposal.id, data.proposal.state, addDiff?.tradeoffBlocks);
    }
  }, [data]);

  const handleInitOrRescheduleProposal = (proposal: Omit<CurrentProposal, "initialized">) => {
    dispatch({ type: "InitOrRescheduleProposal", proposal });
  };

  const handleUpdateTitle = useCallback(
    (title: string) => {
      dispatch({ type: "UpdateTitle", title });
    },
    [dispatch],
  );

  const handleUpdateTime = (startTime: DateTime, endTime: DateTime) => {
    dispatch({ type: "UpdateTime", startTime, endTime });
  };

  const handleUpdateAllDay = (allDay: boolean) => {
    dispatch({ type: "UpdateAllDay", allDay });
  };

  const handleClearProposal = () => {
    dispatch({ type: "ClearProposal" });
  };

  const handleUpdateDescription = useCallback(
    (description: string) => {
      dispatch({ type: "UpdateDescription", description });
    },
    [dispatch],
  );

  const handleSetAttendeeCalendarIds = (initialAttendeeIds: string[]) => {
    dispatch({ type: "SetAttendeeCalendarIds", initialAttendeeIds });
  };

  const handleSetAttendees = (attendees: Attendee[]) => {
    dispatch({ type: "SetAttendees", attendees });
  };

  const handleAddAttendee = (attendee: Attendee) => {
    dispatch({ type: "AddAttendee", attendee });
  };

  const handleRemoveAttendee = (email: string) => {
    dispatch({ type: "RemoveAttendee", email });
  };

  const handleUpdateAttendeeOptionality = (email: string, isOptional: boolean) => {
    dispatch({ type: "UpdateAttendeeOptionality", email, isOptional });
  };

  const handleUpdateRecurrenceRule = (recurrenceRule: RecurrenceRule) => {
    dispatch({ type: "UpdateRecurrenceRule", recurrenceRule });
  };

  const handleUpdateConferenceType = (conferenceType: ConferencingType | null) => {
    dispatch({ type: "UpdateConferenceType", conferenceType });
  };

  const handleUpdateLocation = useCallback(
    (location: string) => {
      dispatch({ type: "UpdateLocation", location });
    },
    [dispatch],
  );

  const handleUpdateFlexDetails = (flexDetails: FlexDetailsInput) => {
    dispatch({ type: "UpdateFlexDetails", flexDetails });
  };

  const handleUpdateProposalMeta = (meta: Partial<CurrentProposal["meta"]>) => {
    dispatch({ type: "UpdateProposalMeta", meta });
  };

  return (
    <WriteContext.Provider
      value={{
        initializeProposal: handleInitializeProposal,
        initOrRescheduleProposal: handleInitOrRescheduleProposal,
        updateTitle: handleUpdateTitle,
        clearProposal: handleClearProposal,
        updateTime: handleUpdateTime,
        updateAllDay: handleUpdateAllDay,
        updateDescription: handleUpdateDescription,
        setAttendeeCalendarIds: handleSetAttendeeCalendarIds,
        setAttendees: handleSetAttendees,
        addAttendee: handleAddAttendee,
        removeAttendee: handleRemoveAttendee,
        updateAttendeeOptionality: handleUpdateAttendeeOptionality,
        updateRecurrenceRule: handleUpdateRecurrenceRule,
        updateConferenceType: handleUpdateConferenceType,
        updateLocation: handleUpdateLocation,
        updateFlexDetails: handleUpdateFlexDetails,
        updateProposalMeta: handleUpdateProposalMeta,
      }}
    >
      <ReadContext.Provider value={{ currentProposal: proposal }}>{children}</ReadContext.Provider>
    </WriteContext.Provider>
  );
};

export const useCurrentProposal = () => useContext(ReadContext);
export const useUpdateCurrentProposal = () => {
  const context = useContext(WriteContext);
  invariant(context, "useUpdateCurrentProposal must be within CurrentProposalProvider");
  return context;
};
